import React, { ChangeEvent } from 'react';
import Select from 'react-select';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import Search from 'src/components/Search/Search';
import Callout from 'src/components/InformationCallout/Callout';
import BulkSelectAllRow from 'src/components/BulkSelectAllRow/BulkSelectAllRow';
import { IProperty } from 'src/pages/Reporting/ReportingFormTypes';
import { ProjectFilter } from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/CustomHooks/useServiceRequestVisibilityFormState/useServiceRequestVisibilityFormState';
import ServiceRequestRow from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/ServiceRequestRow/ServiceRequestRow';
import ServiceRequestRowSkeleton from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/StepPickServiceRequests/skeletons/ServiceRequestRowSkeleton';
import './step-pick-service-requests.scss';

interface SelectOption {
  label: string;
  value: string;
}

const ALL_PROPERTIES_OPTION: SelectOption = {
  label: 'All properties',
  value: 'all',
};

const PICK_SR_SKELETON_ROW_COUNT: number = 6;

interface StepPickServiceRequestsProps {
  projectFilter: ProjectFilter;
  searchQuery: string;
  availableProjects: IProperty[];
  filteredServiceRequests: IServiceRequest[];
  selectedIds: Set<string>;
  isLoadingServiceRequests: boolean;
  serviceRequestsError: string | null;
  onProjectFilterChange: (projectId: ProjectFilter) => void;
  onSearchQueryChange: (query: string) => void;
  onToggleSelection: (id: string) => void;
  onSetSelectAll: (ids: string[], selected: boolean) => void;
}

const StepPickServiceRequests = ({
  projectFilter,
  searchQuery,
  availableProjects,
  filteredServiceRequests,
  selectedIds,
  isLoadingServiceRequests,
  serviceRequestsError,
  onProjectFilterChange,
  onSearchQueryChange,
  onToggleSelection,
  onSetSelectAll,
}: StepPickServiceRequestsProps) => {
  const projectOptions: SelectOption[] = [
    ALL_PROPERTIES_OPTION,
    ...availableProjects.map((project: IProperty) => ({
      label: project.name,
      value: project.id,
    })),
  ];

  const selectedProjectOption: SelectOption =
    projectOptions.find(
      (option: SelectOption) => option.value === projectFilter
    ) ?? ALL_PROPERTIES_OPTION;

  const handleProjectChange = (option: SelectOption | null): void => {
    if (option) {
      onProjectFilterChange(option.value as ProjectFilter);
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onSearchQueryChange(event.target.value);
  };

  const filteredIds: string[] = filteredServiceRequests.map(
    (sr: IServiceRequest) => sr.id
  );

  const filteredCount: number = filteredServiceRequests.length;

  const allFilteredSelected: boolean =
    filteredCount > 0 &&
    filteredServiceRequests.every((sr: IServiceRequest) =>
      selectedIds.has(sr.id)
    );

  const selectedCount: number = selectedIds.size;

  const handleSelectAllToggle = (): void => {
    onSetSelectAll(filteredIds, !allFilteredSelected);
  };

  const selectAllLabel: string = `Select all visible (${filteredCount})`;
  const selectionMetaLabel: string =
    selectedCount > 0
      ? `${selectedCount} selected`
      : 'No service requests selected';

  const showLoader: boolean = isLoadingServiceRequests;
  const showError: boolean =
    !isLoadingServiceRequests && Boolean(serviceRequestsError);
  const showEmpty: boolean =
    !isLoadingServiceRequests && !serviceRequestsError && filteredCount === 0;
  const showList: boolean =
    !isLoadingServiceRequests && !serviceRequestsError && filteredCount > 0;

  return (
    <div className="psr-step">
      <Callout
        variant="info"
        title="Internal-only requests are hidden from homeowners"
        subtitle="Only Property Developers and Admins can see internal-only service requests. External users — homeowners, realtors, and strata / property managers — won't see them anywhere in their portal. Make a request internal-only to keep inspection findings, internal notes, or work that isn't ready to share private; set it back to external when you want the homeowner to see it again."
      />
      <div className="psr-header">
        <div className="psr-filters">
          <div className="psr-filter psr-filter-search">
            <Search
              placeholder="Search ticket number, subject, or property"
              value={searchQuery}
              onChange={handleSearchChange}
              size={undefined}
              theme={undefined}
            />
          </div>

          <div className="psr-filter psr-filter-select">
            <Select
              value={selectedProjectOption}
              onChange={handleProjectChange}
              options={projectOptions}
              placeholder="Property"
              isSearchable
              classNamePrefix="psr-react-select"
            />
          </div>
        </div>

        <BulkSelectAllRow
          checked={allFilteredSelected}
          onChange={handleSelectAllToggle}
          label={selectAllLabel}
          disabled={filteredCount === 0}
          rightSlot={selectionMetaLabel}
        />
      </div>

      <div className="psr-list">
        {showLoader &&
          Array.from(
            { length: PICK_SR_SKELETON_ROW_COUNT },
            (_, index: number) => (
              <ServiceRequestRowSkeleton key={`psr-row-skeleton-${index}`} />
            )
          )}

        {showError && (
          <div className="psr-state psr-state-error">
            <div className="psr-state-heading">
              We couldn't load service requests
            </div>
            <div className="psr-state-body">{serviceRequestsError}</div>
          </div>
        )}

        {showEmpty && (
          <div className="psr-state">
            <div className="psr-state-heading">No service requests match</div>
            <div className="psr-state-body">
              Try clearing the property or search filters.
            </div>
          </div>
        )}

        {showList &&
          filteredServiceRequests.map((sr: IServiceRequest) => (
            <ServiceRequestRow
              key={sr.id}
              serviceRequest={sr}
              isChecked={selectedIds.has(sr.id)}
              onToggle={onToggleSelection}
            />
          ))}
      </div>
    </div>
  );
};

export default StepPickServiceRequests;